/**
 * @use : For internal use within Shipway Team for JIRA ananlytics
 * @author : Aayush Gupta <aayush.gupta@shipway.com>
 * @since : May 15 2025
 */
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useNavigate
} from 'react-router-dom';
import { useEffect, useState } from 'react';

import HomePage from './pages/HomePage';
import TrackerPage from './pages/TrackerPage';
import StageTrackerPage from './pages/StageTrackerPage';
import TeamTrackerPage from './pages/TeamTrackerPage';
import LoginPage from './pages/LoginPage';
import PrivateRoute from './PrivateRoute';
import { getLSWithExpiry } from './helpers';

import 'bootstrap/dist/css/bootstrap.min.css';
import './css/Navbar.css';
import logo from './logo.webp';
import './App.css';
import BACKEND_BASE_URL from './config';

function Navbar() {
  const navigate = useNavigate();
  const authUser = getLSWithExpiry('authKey');

  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState('');

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`${BACKEND_BASE_URL}/sprint/list`, {
            headers: {
                'ngrok-skip-browser-warning': 'true',
            },
        });
        const data = await response.json();
        const options = data.data || [];
        const sprintData = JSON.parse(localStorage.getItem('sprintData'));
        let storedSprintId = false;
        if(sprintData && sprintData.sprint_id){
          storedSprintId = sprintData?.sprint_id;
        }
        setDropdownOptions(options);
        if (storedSprintId) {
          setSelectedOptionId(storedSprintId.toString());
        }
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      }
    };

    fetchOptions();
  }, []);

  const handleOptionChange = (event) => {
    const selectedIndex = event.target.selectedIndex;
    const selectedData = event.target.options[selectedIndex].dataset;
    const selectedId = event.target.value;
    if(!selectedId || (selectedId === 0)){
      alert('Sprint selection is Mandatory');
      return false;
    }
    setSelectedOptionId(selectedId);

    localStorage.setItem(
      'sprintData',
      JSON.stringify({ sprint_id: selectedId, ...selectedData })
    );
    window.location.reload();
  };

  const handleLogout = () => {
    localStorage.removeItem('authKey');
    navigate('/login');
  };

  return (
    <nav className="navbar navbar-expand-lg px-3">
      <NavLink to="/" className="navbar-brand d-flex align-items-center">
        <img src={logo} alt="Logo" height="36" className="me-2" />
        <span>JIRA Dashboard</span>
      </NavLink>

      {authUser && (
        <>
          <div className="navbar-nav">
            <NavLink exact="true" to="/" className="nav-link" activeclassname="active">
              Home
            </NavLink>
            <NavLink to="/issue-tracker" className="nav-link" activeclassname="active">
              Issue Tracker
            </NavLink>
            <NavLink to="/team-tracker" className="nav-link" activeclassname="active">
              Team Tracker
            </NavLink>
            <NavLink to="/status-tracker" className="nav-link" activeclassname="active">
              Status Tracker
            </NavLink>
          </div>
          <div className="d-flex">
            <select
              className="form-select me-3"
              value={selectedOptionId}
              onChange={handleOptionChange}
            >
              <option value="">Select Sprint</option>
              {dropdownOptions.map((sprint) => (
                <option
                  key={sprint.id}
                  value={sprint.id}
                >
                  {sprint.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ms-auto d-flex align-items-center">
            <button className="btn logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </>
      )}
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-light text-center text-muted py-3 border-top">
      <div className="container">
        <small>
          &copy; {new Date().getFullYear()} Shipway • JIRA Dashboard • All rights reserved
        </small>
      </div>
    </footer>
  );
}

function App() {
  return (
    <Router>
      <div className="app-wrapper">
        <Navbar />
        <div className="app-content">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <HomePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/issue-tracker/:key?"
              element={
                <PrivateRoute>
                  <TrackerPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/team-tracker"
              element={
                <PrivateRoute>
                  <TeamTrackerPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/status-tracker"
              element={
                <PrivateRoute>
                  <StageTrackerPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
